import { VStack } from '@ayble-health/dashboard-components';
import { SideNavButton } from './SideNavButton';
import { SideNavLocation } from './types';
import styled from '@emotion/styled';
import { useLocation, useNavigate } from 'react-router-dom';
import { useCallback } from 'react';
import type { EmotionTheme } from '../../theme/emotionTheme';

const GradientBox = styled('div')<{ theme?: EmotionTheme }>`
  background-image: ${({ theme }) =>
    `linear-gradient(to bottom, ${theme.colors.secondary['500']}, ${theme.colors.secondary['200']})`};
`;

const normalizeLocation = (location: SideNavLocation) => {
  switch (location) {
    case SideNavLocation.Home:
      return '/provider-home';
    case SideNavLocation.Care:
      return '/hc-dashboard';
    case SideNavLocation.Calendar:
      return '/calendar';
    case SideNavLocation.Support:
      return '/support';
    default:
      throw new Error(`Unknown location: ${location}`);
  }
};

export const SideNav = () => {
  const { pathname, search } = useLocation();
  const navigate = useNavigate();

  // A patient profile sits under /provider-home/patient/, but it is reachable
  // from the Care roster too. Without this, opening a patient from Care lights
  // up Home — the user would be looking at a Care patient while the nav
  // insists they are somewhere else. The roster tags those links with
  // `?from=hc-dashboard`; see HealthCoachDashboardListItem.
  const openedFromCare =
    new URLSearchParams(search).get('from') === 'hc-dashboard';

  const changeLocation = useCallback(
    (location: SideNavLocation) => {
      const normalizedLocation = normalizeLocation(location);
      navigate(normalizedLocation);
    },
    [navigate]
  );

  return (
    <GradientBox>
      <VStack
        alignItems={'center'}
        justifyContent={'space-between'}
        h={'100%'}
        w={'64px'}
      >
        <VStack>
          <SideNavButton
            isSelected={pathname.includes('/provider-home') && !openedFromCare}
            location={SideNavLocation.Home}
            onLocationSelect={changeLocation}
          />
          <SideNavButton
            isSelected={
              pathname.includes('/hc-dashboard') ||
              pathname.includes('/user-details') ||
              openedFromCare
            }
            location={SideNavLocation.Care}
            onLocationSelect={changeLocation}
          />
          <SideNavButton
            isSelected={pathname.includes('/calendar')}
            location={SideNavLocation.Calendar}
            onLocationSelect={changeLocation}
          />
        </VStack>

        <SideNavButton
          isSelected={pathname.includes(SideNavLocation.Support.toLowerCase())}
          location={SideNavLocation.Support}
          onLocationSelect={changeLocation}
        />
      </VStack>
    </GradientBox>
  );
};
